import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  MagnifyingGlass, 
  BookOpen, 
  Database, 
  Wrench, 
  Chat, 
  Envelope,
  PhoneTransfer, 
  Code, 
  NotePencil, 
  Hash,
  Eye,
  Plus
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const BrainItemSelector = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  categories = [],
  selectedItems = [],
  onCreateNew
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [filteredItems, setFilteredItems] = useState([]);

  // Filter types
  const filterTypes = [
    { value: 'all', label: 'All Items', icon: BookOpen },
    { value: 'simple_answer', label: 'Simple Answers', icon: BookOpen },
    { value: 'complex_answer', label: 'Complex Answers', icon: Database },
    { value: 'simple_action', label: 'Simple Actions', icon: Wrench }
  ];

  // Extract all brain items from categories
  useEffect(() => {
    const allItems = [];

    categories.forEach(category => {
      if (category.settings) {
        category.settings.forEach(setting => {
          allItems.push({
            ...setting,
            category_name: category.name,
            display_type: getDisplayType(setting)
          });
        });
      }
    });

    // Filter by type and search term
    const filtered = allItems.filter(item => {
      const matchesType = activeType === 'all' || item.original_type === activeType;
      const matchesSearch = !searchTerm || 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.category_name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesType && matchesSearch;
    });

    setFilteredItems(filtered);
  }, [categories, activeType, searchTerm]);

  const getDisplayType = (item) => {
    switch (item.original_type) {
      case 'simple_answer':
        return 'Simple Answer';
      case 'complex_answer':
        return 'Complex Answer';
      case 'simple_action':
        return `Simple Action (${item.action_type || 'Unknown'})`;
      default:
        return 'Unknown';
    }
  };

  const getItemIcon = (item) => {
    switch (item.original_type) {
      case 'simple_answer':
        return BookOpen;
      case 'complex_answer':
        return Database;
      case 'simple_action':
        switch (item.action_type) {
          case 'sms':
            return Chat;
          case 'email':
            return Envelope;
          case 'transfer':
            return PhoneTransfer;
          case 'api':
            return Code;
          case 'note':
            return NotePencil;
          case 'dtmf':
            return Hash;
          default:
            return Wrench;
        }
      default:
        return BookOpen;
    }
  };

  const getItemColor = (item) => {
    switch (item.original_type) {
      case 'simple_answer':
        return 'bg-blue-100 text-blue-700';
      case 'complex_answer':
        return 'bg-purple-100 text-purple-700';
      case 'simple_action':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const handleItemSelect = (item) => {
    onSelect({
      reference_type: item.original_type,
      reference_id: item.id,
      name: item.name,
      description: item.description,
      category_name: item.category_name,
      action_type: item.action_type, // Pass the action_type for simple_action items
      subtype: item.action_type // Also set as subtype for backward compatibility
    });
    onClose();
  };

  const isItemSelected = (itemId) => {
    return selectedItems.some(selected => selected.reference_id === itemId);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10010] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              Select Brain Item
            </h2>
            <p className="text-sm text-gray-500">
              Choose existing brain items to reference in this workflow step
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Create New Button */}
            {onCreateNew && (
              <Button
                onClick={onCreateNew}
                className="bg-green-600 hover:bg-green-700 text-white text-sm px-3 py-2"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create New
              </Button>
            )}
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search brain items..."
                className="pl-10"
              />
            </div>

            {/* Type Filters */}
            <div className="flex gap-2 overflow-x-auto">
              {filterTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setActiveType(type.value)}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      activeType === type.value
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-1.5" />
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg font-medium mb-2">No items found</p>
              <p className="text-gray-400 text-sm">
                {searchTerm ? 'Try adjusting your search terms' : 'No brain items available for selection'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => {
                const Icon = getItemIcon(item);
                const isSelected = isItemSelected(item.id);

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'border-indigo-200 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    }`}
                    onClick={() => handleItemSelect(item)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-2 rounded-lg ${getItemColor(item)} flex-shrink-0`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center">
                          <Plus className="h-3 w-3 text-white transform rotate-45" />
                        </div>
                      )}
                    </div>

                    <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
                      {item.name}
                    </h3>

                    <p className="text-xs text-gray-500 mb-2">
                      {item.display_type} • {item.category_name}
                    </p>

                    {item.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {item.description}
                      </p>
                    )}

                    {item.triggers && item.triggers.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.triggers.slice(0, 2).map((trigger, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                          >
                            {trigger}
                          </span>
                        ))}
                        {item.triggers.length > 2 && (
                          <span className="text-xs text-gray-400">
                            +{item.triggers.length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} available
            </p>
            <Button
              onClick={onClose}
              variant="outline"
              className="text-gray-700"
            >
              Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BrainItemSelector;